import { AlertTriangle, ArrowRight, CalendarCheck, CalendarRange, Landmark, TrendingUp, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { InfoCard, StateView, StatusBadge } from "../components";
import { hasTechnicalPending } from "../dashboard-work-orders";
import { dateTime, useApiData } from "../hooks";
import { subtractMoney, workOrderNetAmount } from "../money";
import { MoneyVisibilityButton, useMoneyVisibility } from "../money-visibility";
import type { CursorPage, DashboardPeriod, DashboardSummary, WorkOrder } from "../types";

const civilDate = (value: Date) => new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit",
}).format(value);
export function DashboardPage() {
  const { data: orderPage, error: orderError, loading: ordersLoading } = useApiData<CursorPage<WorkOrder>>("/work-orders?pageSize=100");
  const now = new Date();
  const asOf = civilDate(now);
  const { money } = useMoneyVisibility();
  const { data: summary, error: summaryError, loading: summaryLoading } = useApiData<DashboardSummary>(`/dashboard/summary?asOf=${asOf}`);
  const orders = orderPage?.data ?? [];
  const todayOrders = orders.filter(({ scheduledStart }) => civilDate(new Date(scheduledStart)) === asOf);
  const inField = orders.filter(({ operationalStatus }) => ["traveling", "on_site", "in_progress", "paused"].includes(operationalStatus));
  const pending = orders.filter(hasTechnicalPending);
  const period = (key: DashboardPeriod["key"]) => summary?.periods.find((item) => item.key === key);
  const week = period("week");
  const month = period("month");
  const year = period("year");

  return <StateView loading={ordersLoading || summaryLoading} error={orderError || summaryError}>
    <header className="dashboard-heading">
      <div className="app-container dashboard-heading-content">
        <div>
          <h1>Visão geral</h1>
          <p>{new Date(`${asOf}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</p>
        </div>
        <MoneyVisibilityButton />
      </div>
    </header>

    <section className="dashboard-content">
      <div className="app-container">
        {summary?.financialVisible ? <div className="dashboard-financial-overview">
          <section className="dashboard-metric-group" aria-labelledby="net-result-heading">
            <h2 className="dashboard-metric-heading" id="net-result-heading">Resultado líquido</h2>
            <div className="dashboard-benefits dashboard-metrics dashboard-revenue-metrics">
              <Link className="metric-link" to="/financeiro"><InfoCard icon={TrendingUp} label="Semana" value={money(subtractMoney(week?.received ?? "0.00", week?.costs ?? "0.00"))} tone="blue" /></Link>
              <Link className="metric-link" to="/financeiro"><InfoCard icon={CalendarRange} label="Mês" value={money(subtractMoney(month?.received ?? "0.00", month?.costs ?? "0.00"))} tone="indigo" /></Link>
              <Link className="metric-link" to="/financeiro"><InfoCard icon={Landmark} label="Ano" value={money(subtractMoney(year?.received ?? "0.00", year?.costs ?? "0.00"))} tone="violet" /></Link>
            </div>
          </section>
          <section className="dashboard-metric-group" aria-labelledby="monthly-position-heading">
            <h2 className="dashboard-metric-heading" id="monthly-position-heading">Movimentação e posição</h2>
            <div className="dashboard-benefits dashboard-metrics dashboard-monthly-metrics">
              <Link className="metric-link" to="/financeiro"><InfoCard icon={CalendarCheck} label="Recebido no mês" value={money(month?.received ?? "0.00")} tone="green" /></Link>
              <Link className="metric-link" to="/financeiro"><InfoCard icon={Wrench} label="Custos no mês" value={money(month?.costs ?? "0.00")} tone="red" /></Link>
              <Link className="metric-link" to="/financeiro"><InfoCard icon={AlertTriangle} label="A receber" value={money(summary.position?.receivable ?? "0.00")} tone="amber" /></Link>
            </div>
          </section>
        </div> : <div className="dashboard-benefits dashboard-operational-metrics">
          <InfoCard icon={CalendarCheck} label="Serviços hoje" value={todayOrders.length} />
          <InfoCard icon={Wrench} label="Em campo" value={inField.length} tone="soft" />
          <InfoCard icon={AlertTriangle} label="Pendências técnicas" value={pending.length} tone="red" />
        </div>}

        <div className="dashboard-workspace">
          <section className="workspace-card today-column">
            <div className="section-title-row">
              <div><h2>Agenda de hoje</h2></div>
              <Link className="text-link" to="/agenda">Agenda completa <ArrowRight size={15} /></Link>
            </div>
            {todayOrders.length === 0 ? <div className="compact-empty"><CalendarCheck size={22} /><span>Nenhum atendimento hoje.</span></div> : <div className="schedule-list">
              {todayOrders.slice(0, 6).map((order) => <Link className="schedule-item" to={`/ordens/${order.id}`} key={order.id}>
                <time><strong>{new Date(order.scheduledStart).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</strong><span>{new Date(order.scheduledEnd).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span></time>
                <div className="schedule-copy"><span>OS {order.number}</span><strong>{dateTime(order.scheduledStart)}</strong><small>{money(workOrderNetAmount(order))}</small></div>
                <StatusBadge value={order.operationalStatus} />
                <ArrowRight className="schedule-arrow" size={17} />
              </Link>)}
            </div>}
          </section>

          <aside className="workspace-card attention-column">
            <div className="section-title-row"><div><h2>Pendências</h2></div><strong className="count-badge">{pending.length}</strong></div>
            {pending.length === 0 ? <div className="attention-empty"><CalendarCheck size={22} /><span>Nenhuma pendência técnica.</span></div> : <div className="attention-list">{pending.slice(0, 5).map((order) => <Link to={`/ordens/${order.id}`} key={order.id}><span className="alert-icon"><AlertTriangle size={17} /></span><div><strong>OS {order.number}</strong><span>{order.requiresReturn || order.operationalStatus === "incomplete" ? "Retorno técnico necessário" : "Conclusão aguardando validação"}</span></div><ArrowRight size={16} /></Link>)}</div>}
          </aside>
        </div>
      </div>
    </section>
  </StateView>;
}
